import { Injectable, computed, inject, signal } from '@angular/core';

import { Maybe } from '@orderna/admin-panel/src/types/maybe';

import { AuthApiService } from '../../../core/auth/auth-api.service';
import { StoreTaxRegistrationStatus } from '../../../core/stores/enums/store-tax-registration-status.enum';

import { CashMovementType } from '../../../model/enum/cash-movement-type';
import { DiscountType } from '../../../model/enum/discount-type';
import { PaymentMethod } from '../../../model/enum/payment-method';
import { TransactionStatus } from '../../../model/enum/transaction-status';
import { Transaction } from '../../../model/transaction';

import { LocalStorageService } from '../../../shared/services/local-storage.service';

import { CustomersApiService } from '../../customers/customers-api.service';
import { FindCustomerBnpcStatusResponseDto } from '../../customers/dtos/find-customer-bnpc-status.response.dto';
import { FindCustomerStatutoryDiscountsResponseDto } from '../../customers/dtos/find-customer-statutory-discounts.response.dto';
import { CustomerStatutoryDiscountStatus } from '../../customers/enums/customer-statutory-discount-status';
import { CustomerStatutoryDiscountType } from '../../customers/enums/customer-statutory-discount-type';

import { LiveOrdersApiService } from '../../live-orders/live-orders-api.service';

import { OfflineService } from '../offline/offline.service';
import {
  PosOfflineDbService,
  PosTransactionInvoice,
} from '../offline/pos-offline-db.service';

import { StockLayerGatewayService } from '../../stock-layer-gateway/stock-layer-gateway.service';

import { StoreCartValidationApiService } from '../../store-cart-validation/store-cart-validation-api.service';
import { ValidateCartResponseDto } from '../../store-cart-validation/dtos/validate-cart.response.dto';

import { CreateTransactionRequestDto } from '../../transactions/dtos/create-transaction.request.dto';
import { CreateTransactionResponseDto } from '../../transactions/dtos/create-transaction.response.dto';
import { ParkTransactionRequestDto } from '../../transactions/dtos/park-transaction.request.dto';
import { PreviewTransactionTotalsRequestDto } from '../../transactions/dtos/preview-transaction-totals.request.dto';
import { PreviewTransactionTotalsResponseDto } from '../../transactions/dtos/preview-transaction-totals.response.dto';
import { UnparkTransactionRequestDto } from '../../transactions/dtos/unpark-transaction.request.dto';
import { UnparkTransactionResponseDto } from '../../transactions/dtos/unpark-transaction.response.dto';
import { TransactionsApiService } from '../../transactions/transactions-api.service';
import { CreateTransactionDtoStatus } from '../../transactions/types/create-transaction-dto-status';

import {
  CurrentTransactionCustomer,
  CurrentTransactionCustomerStatutoryDiscount,
  CurrentTransactionDiscountMode,
  CurrentTransactionItem,
  CurrentTransactionNote,
  CurrentTransactionProduct,
  CurrentTransactionPromoItemDiscount,
  CurrentTransactionPromoTransactionDiscount,
  CurrentTransactionSerializedItem,
  CurrentTransactionSerializedItemEntry,
  CurrentTransactionStatutoryDiscount,
  CurrentTransactionStore,
} from './types/current-transaction';
import { TransactionServiceType } from '../../transactions/enums/transaction-service-type.enum';
import { StoreType } from '../../../core/stores/enums/store-type.enum';
import { LiveOrderFulfillmentType } from '../../../model/enum/live-order-fulfillment-type';
import { buildCurrentTransactionItemKey } from './utils/build-current-transaction-item-key';
import { CurrentDeviceService } from '../current-device/current-device.service';
import { ParkTransactionResponseDto } from '../../transactions/dtos/park-transaction.response.dto';
import { CashRegisterDeviceChallengePurpose } from '../../cash-drawers/cash-register-device/enums/cash-register-device-challenge-purpose.enum';
import { DeviceAuthenticatedRequestDto } from '../../cash-drawers/cash-register-device/dtos/partials/device-authenticated.request.dto';
import { CurrentRegisterSessionService } from '../current-register-session/current-register-session.service';

@Injectable({
  providedIn: 'root',
})
export class CurrentTransactionService {
  #store = signal<CurrentTransactionStore>({
    isTaxable: false,
    type: StoreType.RETAIL,
  });

  #customer = signal<CurrentTransactionCustomer>(null);
  #reference = signal<string | null>(null);
  #promoTransactionDiscount =
    signal<CurrentTransactionPromoTransactionDiscount>(null);
  #customerStatutoryDiscounts = signal<
    CurrentTransactionCustomerStatutoryDiscount[]
  >([]);

  #items = signal<Map<string, CurrentTransactionItem>>(new Map());

  #paymentMethod = signal(PaymentMethod.CASH);
  #cashReceived = signal(0);
  #qrphData = signal<{
    paymentIntentId: string;
    txnRefNo?: string;
    instrId?: string;
    traceNo?: string;
    paidAt?: string;
  } | null>(null);

  #bnpcStatusLoading = signal(false);
  #bnpcStatus = signal<FindCustomerBnpcStatusResponseDto | null>(null);
  #bnpcRemainingThisWeek = signal<number | null>(null);
  #bnpcItemsCountThisWeek = signal<number | null>(null);

  #status = signal<CreateTransactionDtoStatus>(TransactionStatus.COMPLETED);
  #note = signal<string | null>('');
  #uuid = signal<Maybe<Transaction['uuid']>>(undefined);
  #includeBuyerInfo = signal<boolean>(false);
  #previewTotals = signal<PreviewTransactionTotalsResponseDto | null>(null);

  #cartValidation = signal<ValidateCartResponseDto>({
    valid: true,
    items: [],
  });

  #submitting = signal(false);
  #serviceType = signal<TransactionServiceType | null>(null);

  store = this.#store.asReadonly();
  customer = this.#customer.asReadonly();
  reference = this.#reference.asReadonly();
  promoTransactionDiscount = this.#promoTransactionDiscount.asReadonly();
  cartValidation = this.#cartValidation.asReadonly();
  cartValidating = signal(false);
  submitting = this.#submitting.asReadonly();
  qrphData = this.#qrphData.asReadonly();
  serviceType = this.#serviceType.asReadonly();

  productCounts = computed(() => {
    const counts = new Map<number, number>();

    for (const item of this.items().values()) {
      counts.set(
        item.product.id,
        (counts.get(item.product.id) ?? 0) + item.quantity,
      );
    }

    return counts;
  });
  valid = computed(() => this.#cartValidation().valid);
  statutoryDiscount = computed(() => {
    const discount = this.customer()?.statutoryDiscount;

    if (!discount) return null;

    return discount.type === CustomerStatutoryDiscountType.NONE
      ? null
      : discount;
  });

  items = this.#items.asReadonly();

  paymentMethod = this.#paymentMethod.asReadonly();
  status = this.#status.asReadonly();
  note = this.#note.asReadonly();
  uuid = this.#uuid.asReadonly();
  bnpcStatusLoading = this.#bnpcStatusLoading.asReadonly();
  bnpcStatus = this.#bnpcStatus.asReadonly();
  bnpcRemainingThisWeek = this.#bnpcRemainingThisWeek.asReadonly();
  bnpcItemsCountThisWeek = this.#bnpcItemsCountThisWeek.asReadonly();
  includeBuyerInfo = this.#includeBuyerInfo.asReadonly();

  #localStorage = inject(LocalStorageService);
  #offlineService = inject(OfflineService);
  #offlineDb = inject(PosOfflineDbService);
  #stockGateway = inject(StockLayerGatewayService);

  #customerApi = inject(CustomersApiService);
  #transactionApi = inject(TransactionsApiService);
  #liveOrdersApi = inject(LiveOrdersApiService);
  #storeCartValidationApi = inject(StoreCartValidationApiService);

  #currentDevice = inject(CurrentDeviceService);
  #currentRegisterSession = inject(CurrentRegisterSessionService);
  #auth = inject(AuthApiService);

  empty = computed(() => this.items().size === 0);
  voidable = computed(() => {
    return this.items().size > 0;
  });
  totalItems = computed(() =>
    Array.from(this.items().values()).reduce(
      (acc, item) => acc + item.quantity,
      0,
    ),
  );
  subtotal = computed(() =>
    Array.from(this.items().entries()).reduce(
      (acc, [_, item]) => acc + item.subtotalLessItemDiscount,
      0,
    ),
  );
  netOfTax = computed(
    () =>
      this.#previewTotals()?.totals.sales.netOfTax ??
      Array.from(this.items().values()).reduce(
        (acc, item) => acc + item.netOfTax,
        0,
      ),
  );
  lessTransactionDiscount = computed(
    () =>
      this.#previewTotals()?.totals.discounts.promotional.transaction ??
      Array.from(this.items().entries()).reduce(
        (acc, [_, item]) =>
          acc +
          (item.isPromoDiscountApplied ? item.lessTransactionDiscount : 0),
        0,
      ),
  );
  lessStatutoryTaxExempted = computed(
    () =>
      this.#previewTotals()?.totals.statutoryTaxExemptAmount ??
      Array.from(this.items().entries()).reduce(
        (acc, [_, item]) =>
          acc +
          (item.isStatutoryDiscountApplied ? item.lessStatutoryTaxExempted : 0),
        0,
      ),
  );
  lessStatutoryDiscount = computed(
    () =>
      this.#previewTotals()?.totals.discounts.statutory ??
      Array.from(this.items().entries()).reduce(
        (acc, [_, item]) =>
          acc +
          (item.isStatutoryDiscountApplied ? item.lessStatutoryDiscount : 0),
        0,
      ),
  );
  tax = computed(
    () =>
      this.#previewTotals()?.totals.tax.taxAmount ??
      Array.from(this.items().entries()).reduce(
        (acc, [_, item]) => acc + item.tax,
        0,
      ),
  );
  taxable = computed(
    () =>
      this.#previewTotals()?.totals.tax.taxableAmount ??
      Array.from(this.items().entries()).reduce(
        (acc, [_, item]) => acc + item.taxable,
        0,
      ),
  );
  taxExempted = computed(
    () =>
      this.#previewTotals()?.totals.tax.taxExemptAmount ??
      Array.from(this.items().entries()).reduce(
        (acc, [_, item]) => acc + item.taxExempted,
        0,
      ),
  );
  zeroRated = computed(
    () =>
      this.#previewTotals()?.totals.tax.zeroRatedAmount ??
      Array.from(this.items().entries()).reduce(
        (acc, [_, item]) => acc + item.zeroRated,
        0,
      ),
  );
  serviceCharge = computed(
    () =>
      this.#previewTotals()?.totals.serviceCharge ??
      Array.from(this.items().entries()).reduce(
        (acc, [_, item]) => acc + item.serviceChargeAmount,
        0,
      ),
  );
  total = computed(
    () =>
      this.#previewTotals()?.totals.totalPayable ??
      Array.from(this.items().entries()).reduce(
        (acc, [_, item]) => acc + item.total,
        0,
      ),
  );
  cartValidationByItemKey = computed(() => {
    const items = new Map<string, ValidateCartResponseDto['items'][number]>();

    for (const item of this.#cartValidation().items) {
      const key = buildCurrentTransactionItemKey({
        id: item.productId,
        selectedModifierOptions: item.selectedModifierOptionIds.map((id) => ({
          id,
        })),
        selectedBundleOptions: item.selectedBundleOptionIds.map((option) => ({
          id: option.id,
          quantity: option.quantity,
          selectedModifierOptions: option.selectedModifierOptionIds.map(
            (id) => ({ id }),
          ),
        })),
      });
      items.set(key, item);
    }

    return items;
  });

  private readonly BNPC_DISCOUNT_ID = 6;
  private readonly BNPC_MIN_ITEMS = 4; // if full Php2500 allowance will be utilized
  bnpcItems = computed(() =>
    [...this.items().values()].filter((item) =>
      item.product.statutoryDiscountIds.includes(this.BNPC_DISCOUNT_ID),
    ),
  );
  bnpcItemsSubtotal = computed(() =>
    this.bnpcItems().reduce((subtotal, item) => subtotal + item.subtotal, 0),
  );
  bnpcItemsCount = computed(() => this.bnpcItems().length);
  hasBnpcItems = computed(() => this.bnpcItemsCount() > 0);
  isCustomerBnpcDiscountEligible = computed(() =>
    this.#customerStatutoryDiscounts().some(
      (o) => o.id === this.BNPC_DISCOUNT_ID,
    ),
  );

  async setReference(reference: string) {
    this.#reference.set(reference);
    this.#localStorage.setPosTransactionReference(reference);
  }

  async setServiceType(serviceType: TransactionServiceType) {
    const storeType = this.#currentDevice.store()?.type;

    if (storeType !== StoreType.FNB) return;

    this.#serviceType.set(serviceType);
    this.#localStorage.setPosTransactionServiceType(serviceType);
  }

  private setCustomerStatutoryDiscounts(
    discounts: FindCustomerStatutoryDiscountsResponseDto,
  ) {
    this.#customerStatutoryDiscounts.set(discounts);
    this.#localStorage.setPosCustomerStatutoryDiscounts(discounts);
  }

  async setCustomer(customer: CurrentTransactionCustomer) {
    this.#customer.set(customer);
    this.#localStorage.setPosCustomer(customer);

    this.#includeBuyerInfo.set(customer != null);
    this.#localStorage.setPosIncludeBuyerInfo(customer != null);

    await this.refreshCustomerStatutoryDiscounts();
    await this.refreshBnpcDiscount();

    await this.refreshItems();
  }

  async setTransactionDiscount(
    discount: CurrentTransactionPromoTransactionDiscount,
  ): Promise<void> {
    this.#promoTransactionDiscount.set(discount);
    this.#localStorage.setPosDiscount(discount ?? null);

    await this.refreshTransactionDiscount();
  }

  canApplyBnpcDiscount() {
    if (
      !this.hasBnpcItems() ||
      !this.isCustomerBnpcDiscountEligible() ||
      this.#bnpcStatusLoading()
    ) {
      return false;
    }

    const bnpcRemainingThisWeek = this.bnpcRemainingThisWeek();
    const bnpcItemsThisWeek = this.bnpcItemsCountThisWeek();

    if (bnpcRemainingThisWeek === null || bnpcItemsThisWeek === null) {
      return false;
    }

    const hasRemainingBalance =
      bnpcRemainingThisWeek > this.bnpcItemsSubtotal();
    if (hasRemainingBalance) return true;

    const meetsMinItems =
      bnpcItemsThisWeek + this.bnpcItemsCount() >= this.BNPC_MIN_ITEMS;

    return meetsMinItems;
  }

  async refreshBnpcDiscount() {
    const customer = this.#customer();

    if (!customer || !this.isCustomerBnpcDiscountEligible()) {
      this.removeBnpcDiscount();
      return;
    }

    try {
      this.#bnpcStatusLoading.set(true);
      const status = await this.#customerApi.findCustomerBnpcStatus(
        customer.id,
      );

      await this.setBnpcDiscount(status);
    } catch (error) {
      console.error('Error loading BNPC discount status', error);
      this.removeBnpcDiscount();
    } finally {
      this.#bnpcStatusLoading.set(false);
    }
  }

  async setBnpcDiscount(
    status: FindCustomerBnpcStatusResponseDto,
    options?: { updateState: boolean },
  ) {
    this.#bnpcStatus.set(status);

    const { updateState = true } = options ?? {};

    if (updateState) {
      await this.setBnpcRemainingThisWeek(status.bnpcRemainingBalance);
      await this.setBnpcItemsCountThisWeek(status.bnpcItemsCount);
    }
  }

  removeBnpcDiscount() {
    this.#bnpcStatus.set(null);

    this.#bnpcRemainingThisWeek.set(null);
    this.#localStorage.setPosBnpcRemainingThisWeek(null);

    this.#bnpcItemsCountThisWeek.set(null);
    this.#localStorage.setPosBnpcItemsCountThisWeek(null);
  }

  async refreshTransactionDiscount() {
    const discount = this.promoTransactionDiscount();
    if (!discount) {
      await this.refreshItems();
      return;
    }

    const discountCopy = { ...discount };
    discountCopy.discountedAmount =
      discount.type === DiscountType.FIXED
        ? Math.min(this.subtotal(), discount.value)
        : this.subtotal() * (discount.value / 100);

    this.#promoTransactionDiscount.set(discountCopy);
    await this.refreshItems();
  }

  setPaymentMethod(paymentMethod: PaymentMethod) {
    this.#paymentMethod.set(paymentMethod);
    this.#localStorage.setPosPaymentMethod(paymentMethod);
  }

  setCashReceived(cashReceived: number) {
    this.#cashReceived.set(cashReceived);
  }

  setQrphData(
    data: {
      paymentIntentId: string;
      txnRefNo?: string;
      instrId?: string;
      traceNo?: string;
      paidAt?: string;
    } | null,
  ) {
    this.#qrphData.set(data);
  }

  setTransactionStatus(status: CreateTransactionDtoStatus) {
    this.#status.set(status);
    this.#localStorage.setPosTransactionStatus(status);
  }

  setTransactionNote(note: string | null) {
    this.#note.set(note);
  }

  setTransactionUuid(uuid: Transaction['uuid']): void {
    this.#uuid.set(uuid);
    this.#localStorage.setPosTransactionUuid(uuid);
  }

  setIncludeBuyerInfo(includeBuyerInfo: boolean) {
    this.#includeBuyerInfo.set(includeBuyerInfo);
    this.#localStorage.setPosIncludeBuyerInfo(includeBuyerInfo);
  }

  async setBnpcRemainingThisWeek(value: number) {
    const status = this.#bnpcStatus();

    const clampedValue = status
      ? Math.min(status.bnpcRemainingBalance, value)
      : value;

    this.#bnpcRemainingThisWeek.set(clampedValue);
    this.#localStorage.setPosBnpcRemainingThisWeek(clampedValue);
  }

  async setBnpcItemsCountThisWeek(value: number) {
    const status = this.#bnpcStatus();
    const clampedValue = status
      ? Math.max(status.bnpcItemsCount, value)
      : value;

    this.#bnpcItemsCountThisWeek.set(clampedValue);
    this.#localStorage.setPosBnpcItemsCountThisWeek(clampedValue);
  }

  async refreshItems() {
    const items = new Map(this.#items());
    const txnDiscount = this.#promoTransactionDiscount();

    let index = 0;
    let size = items.size;
    let txnDiscountRemainder = txnDiscount?.discountedAmount ?? 0;

    const canApplyBnpcDiscount = this.canApplyBnpcDiscount();
    const bnpcRemainingThisWeek = this.bnpcRemainingThisWeek() ?? 0;
    let bnpcDiscountBaseRemainder = bnpcRemainingThisWeek;

    for (const [_, item] of items.entries()) {
      // Allocate transaction discount
      if (txnDiscount) {
        const allocationRatio = item.subtotalLessItemDiscount / this.subtotal();
        let allocation = txnDiscountRemainder * allocationRatio;

        if (index === size - 1) {
          allocation = txnDiscountRemainder;
        }

        item.promoTransactionDiscount = {
          ...txnDiscount,
          discountedAmount: allocation,
        };

        txnDiscountRemainder -= allocation;
      } else {
        item.promoTransactionDiscount = null;
      }

      item.statutoryDiscount = this.getItemStatutoryDiscount(
        item,
        canApplyBnpcDiscount,
      );

      // Allocate BNPC discount
      if (
        item.statutoryDiscount &&
        item.statutoryDiscount.id === this.BNPC_DISCOUNT_ID &&
        canApplyBnpcDiscount
      ) {
        const discountBase = item.subtotal - item.lessStatutoryTaxExempted;
        const maxDiscountBase = Math.min(
          discountBase,
          bnpcDiscountBaseRemainder,
        );
        item.statutoryDiscount.maxDiscountBase = maxDiscountBase;
        bnpcDiscountBaseRemainder -= maxDiscountBase;
      }

      item.discountMode = this.getItemDiscountMode(item);

      index++;
    }

    this.#items.set(items);
    const serializableItems = this.itemMapToSerializable(this.items());
    this.#localStorage.setPosItems(serializableItems);

    await this.previewTotals();
  }

  async addItem(
    product: CurrentTransactionProduct,
    quantity: number = 1,
  ): Promise<void> {
    const key = buildCurrentTransactionItemKey(product);

    const existingItem = this.items().get(key);

    if (existingItem) {
      existingItem.quantity += 1;

      await this.saveItem(existingItem);
      return;
    }

    const item = this.createItem({
      product,
      quantity,
    });

    await this.saveItem(item);
  }

  createItem({
    product,
    quantity,
    note = null,
    promoItemDiscount = null,
    promoTransactionDiscount = null,
    statutoryDiscount = null,
  }: {
    product: CurrentTransactionProduct;
    quantity: number;
    note?: CurrentTransactionNote;
    promoItemDiscount?: CurrentTransactionPromoItemDiscount;
    promoTransactionDiscount?: CurrentTransactionPromoTransactionDiscount;
    statutoryDiscount?: CurrentTransactionStatutoryDiscount;
  }): CurrentTransactionItem {
    return new CurrentTransactionItem(
      this.#store(),
      product,
      quantity,
      note,
      promoItemDiscount,
      promoTransactionDiscount,
      statutoryDiscount,
    );
  }

  async saveItem(item: CurrentTransactionItem): Promise<void> {
    this.#items.update((items) => {
      const map = new Map(items);
      map.set(item.key, item);
      return map;
    });

    await this.validateCart();
    await this.refreshTransactionDiscount();
  }

  async validateCart() {
    const storeId = this.#currentDevice.storeId();

    if (!storeId) {
      console.error('No store ID. Cannot validate cart.');
      return;
    }

    const items = [...this.items().values()];

    if (items.length == 0) return;

    this.cartValidating.set(true);
    try {
      const response = await this.#storeCartValidationApi.validateCart(
        storeId,
        {
          items: items.map((item) => ({
            productId: item.product.id,
            modifierOptionIds: (item.product.selectedModifierOptions ?? []).map(
              (o) => o.id,
            ),
            bundleOptions: (item.product.selectedBundleOptions ?? []).map(
              (bundleItem) => ({
                id: bundleItem.id,
                quantity: bundleItem.quantity,
                modifierOptionIds: bundleItem.selectedModifierOptions.map(
                  (o) => o.id,
                ),
              }),
            ),
            quantity: item.quantity,
          })),
        },
      );

      this.#cartValidation.set(response);
    } finally {
      this.cartValidating.set(false);
    }
  }

  async setItems(items: CurrentTransactionItem[]): Promise<void> {
    this.#items.set(
      items.reduce(
        (map, item) =>
          map.set(buildCurrentTransactionItemKey(item.product), item),
        new Map<string, CurrentTransactionItem>(),
      ),
    );

    const serializableItems = this.itemMapToSerializable(this.items());
    this.#localStorage.setPosItems(serializableItems);

    await this.refreshItems();
  }

  async removeTransactionItem(key: string): Promise<void> {
    this.#items.update((transactionList) => {
      transactionList.delete(key);
      return new Map(transactionList);
    });

    const serializableItems = this.itemMapToSerializable(this.items());
    this.#localStorage.setPosItems(serializableItems);

    if (!this.items().size) {
      this.reset();
    }

    await this.validateCart();
    await this.refreshTransactionDiscount();
  }

  removeReference(): void {
    this.#reference.set(null);
    this.#localStorage.setPosTransactionReference(null);
  }

  async removeCustomer(): Promise<void> {
    this.#customer.set(null);
    this.#includeBuyerInfo.set(false);
    this.#customerStatutoryDiscounts.set([]);
    this.#bnpcRemainingThisWeek.set(null);
    this.#bnpcItemsCountThisWeek.set(null);

    this.#localStorage.setPosCustomer(null);
    this.#localStorage.setPosIncludeBuyerInfo(false);
    this.#localStorage.setPosCustomerStatutoryDiscounts([]);
    this.#localStorage.setPosBnpcRemainingThisWeek(null);
    this.#localStorage.setPosBnpcItemsCountThisWeek(null);

    await this.refreshItems();
  }

  reset(): void {
    this.#uuid.set(undefined);

    this.#reference.set(null);
    this.#customer.set(null);
    this.#customerStatutoryDiscounts.set([]);
    this.#promoTransactionDiscount.set(null);
    this.#cashReceived.set(0);
    this.#qrphData.set(null);
    this.#bnpcStatus.set(null);
    this.#bnpcRemainingThisWeek.set(null);
    this.#bnpcItemsCountThisWeek.set(null);

    this.#status.set(TransactionStatus.COMPLETED);
    this.#items.set(new Map());
    this.#paymentMethod.set(PaymentMethod.CASH);

    this.#localStorage.clearPosState();
  }

  async init() {
    // Get POS data from local storage
    const discount = this.#localStorage.getPosDiscount();
    const customer = this.#localStorage.getPosCustomer();
    const statutoryDiscounts =
      this.#localStorage.getPosCustomerStatutoryDiscounts();
    const includeBuyerInfo = this.#localStorage.getPosIncludeBuyerInfo();
    const bnpcRemainingThisWeek =
      this.#localStorage.getPosBnpcRemainingThisWeek();
    const bnpcItemsThisWeek = this.#localStorage.getPosBnpcItemsThisWeek();
    const reference = this.#localStorage.getPosTransactionReference();
    const serviceType = this.#localStorage.getPosTransactionServiceType();

    const items = this.#localStorage.getPosItems();

    const uuid = this.#localStorage.getPosTransactionUuid();
    const paymentMethod = this.#localStorage.getPosPaymentMethod();
    const status = this.#localStorage.getPosTransactionStatus();

    // Set data
    this.initStore();
    this.initServiceType(serviceType);

    this.#customer.set(customer);
    this.#reference.set(reference);

    this.#promoTransactionDiscount.set(discount);
    this.#customerStatutoryDiscounts.set(statutoryDiscounts);
    this.#includeBuyerInfo.set(includeBuyerInfo);

    this.#items.set(items ? this.serializableToItemMap(items) : new Map());
    this.#paymentMethod.set(paymentMethod ?? PaymentMethod.CASH);
    this.#uuid.set(uuid);
    this.#status.set(status ?? TransactionStatus.COMPLETED);

    // Refresh discounts data
    await this.refreshCustomerStatutoryDiscounts();

    await this.refreshBnpcDiscount();
    const bnpcStatus = this.bnpcStatus();
    if (bnpcStatus && bnpcRemainingThisWeek && bnpcItemsThisWeek) {
      this.#bnpcRemainingThisWeek.set(
        Math.min(bnpcStatus.bnpcRemainingBalance, bnpcRemainingThisWeek),
      );
      this.#bnpcItemsCountThisWeek.set(
        Math.max(bnpcStatus.bnpcItemsCount, bnpcItemsThisWeek),
      );
    }

    // Validate data before totals are computed
    await this.validateCart();

    // Given all necessary data are provided and validated,
    // refresh cart items so totals are computed
    await this.refreshItems();
  }

  private initStore() {
    const store = this.#currentDevice.store();

    if (!store) return;

    this.#store.set({
      isTaxable: store.taxRegistrationStatus === StoreTaxRegistrationStatus.VAT,
      type: store.type,
      serviceChargeRate: store.serviceCharge,
    });
  }

  private initServiceType(serviceType: TransactionServiceType | null): void {
    const storeType = this.#currentDevice.store()?.type ?? StoreType.RETAIL;

    if (serviceType) {
      this.#serviceType.set(serviceType);
      return;
    }

    const defaultServiceTypeMap = {
      [StoreType.FNB]: TransactionServiceType.DINE_IN,
      [StoreType.RETAIL]: null,
    };

    this.#serviceType.set(defaultServiceTypeMap[storeType]);
  }

  private async unpark(): Promise<UnparkTransactionResponseDto> {
    const uuid = this.uuid();

    if (!uuid) {
      throw new Error('No transaction UUID.');
    }

    if (uuid.startsWith('parked-') || this.#offlineService.isOffline()) {
      return this.unparkOffline(uuid);
    }

    // Build the request before park() because park() resets the transaction.
    const data = this.toUnparkTransactionRequestDto();

    // TODO: Save only if there are changes.
    await this.park();

    const response = await this.#transactionApi.unparkTransaction(uuid, data);
    this.reset();

    return response;
  }

  async cancel(reason: string): Promise<void> {
    if (this.#offlineService.isOffline()) {
      await this.cancelOffline(reason);
      return;
    }

    const uuid = this.uuid();
    if (uuid) {
      // Cancel the existing parked transaction.
      await this.#transactionApi.cancelTransaction(uuid, { reason });
      this.reset();
      return;
    }

    // Create new transaction with cancelled status
    this.#status.set(TransactionStatus.CANCELLED);
    await this.submit();
  }

  async park(): Promise<ParkTransactionResponseDto> {
    if (this.#offlineService.isOffline()) {
      return this.parkOffline();
    }

    const uuid = this.uuid();

    if (uuid) {
      // Update the existing parked transaction.
      const data = this.toParkTransactionRequestDto();
      const response = await this.#transactionApi.parkTransaction(uuid, data);
      this.reset();
      return response;
    }

    // Create a new transaction with parked status.
    this.#status.set(TransactionStatus.PARKED);
    return this.submit();
  }

  async previewTotals() {
    this.#previewTotals.set(null);

    if (!this.valid()) return;

    if (this.items().size === 0) return;

    if (this.#offlineService.isOffline()) return;

    try {
      const data = this.toPreviewTransactionTotalsRequestDto();
      const previewTotals =
        await this.#transactionApi.previewTransactionTotals(data);

      this.#previewTotals.set(previewTotals);
    } catch (error) {
      console.error('Error fetching totals from backend', error);
    }
  }

  async submit(
    offlineInvoice?: PosTransactionInvoice,
  ): Promise<CreateTransactionResponseDto> {
    await this.validateCart();

    if (!this.valid()) {
      throw new Error(
        'Some items in this cart are no longer available in the requested quantity. Please review the items before proceeding.',
      );
    }

    if (this.uuid()) {
      // A UUID indicates this is an existing parked transaction.
      // Unpark to change transaction's status to complete.
      return this.unpark();
    }

    if (this.#offlineService.isOffline()) {
      return this.submitOffline(offlineInvoice);
    }

    try {
      this.#submitting.set(true);

      const { submitResponse, submittedData } =
        await this.#currentDevice.executeWithDeviceVerification(
          CashRegisterDeviceChallengePurpose.SUBMIT_TRANSACTION,
          async (signedChallenge) => {
            const data = this.toCreateTransactionRequestDto(signedChallenge);
            const submitResponse =
              await this.#transactionApi.createTransaction(data);

            return { submitResponse, submittedData: data };
          },
        );

      // Create live order from transaction
      if (
        this.#store().type === StoreType.FNB &&
        submitResponse.status === TransactionStatus.COMPLETED
      ) {
        await this.#currentRegisterSession.refresh();
        await this.createLiveOrder(
          submitResponse,
          submittedData,
          offlineInvoice,
        );
      }

      this.reset();

      return submitResponse;
    } catch (error) {
      throw error; // let caller handle the error
    } finally {
      this.#submitting.set(false);
    }
  }

  private async submitOffline(invoice?: PosTransactionInvoice) {
    await this.#offlineDb.saveTransaction({
      items: this.itemMapToSerializable(this.items()),
      discount: this.promoTransactionDiscount(),
      cashReceived: this.#cashReceived(),
      expectedTotalPayable: this.total(),
      bnpcRemainingBalanceThisWeek: this.bnpcRemainingThisWeek() ?? 0,
      bnpcItemsCountThisWeek: this.bnpcItemsCountThisWeek() ?? 0,
      paymentMethod: this.paymentMethod(),
      customer: this.customer(),
      status: this.status(),
      synced: 0,
      createdAt: Date.now(),
      createdWhileOffline: true,
      storeId: this.#currentDevice.storeId()!,
      registerId: this.#currentDevice.registerId()!,
      type: 'counter',
      staffId: this.#auth.currentUserId()!,
      salesTaxRate: 12,
      tip: 0,
      note: this.note() ?? undefined,
      invoice,
    });

    await this.updateOfflineProductStocks();
    await this.updateOfflineRegisterSession();

    this.reset();

    return {
      uuid: 'offline-' + Date.now(),
      status: this.status(),
    } as CreateTransactionResponseDto;
  }

  private async unparkOffline(
    uuid: string,
  ): Promise<UnparkTransactionResponseDto> {
    if (uuid.startsWith('parked-')) {
      await this.#offlineDb.removeParkedTransaction(uuid);
    }

    this.#uuid.set(undefined);
    this.#status.set(TransactionStatus.COMPLETED);

    return this.submit() as Promise<UnparkTransactionResponseDto>;
  }

  private async parkOffline(): Promise<ParkTransactionResponseDto> {
    const existingUuid = this.uuid();
    const parkedUuid = existingUuid || `parked-${Date.now()}`;

    const transactionData = {
      items: this.itemMapToSerializable(this.items()),
      discount: this.promoTransactionDiscount(),
      paymentMethod: this.paymentMethod(),
      customer: this.customer(),
      status: TransactionStatus.PARKED,
      note: this.note(),
      createdAt: Date.now(),
    };

    await this.#offlineDb.saveParkedTransaction(
      parkedUuid,
      this.#currentDevice.storeId()!,
      this.#currentDevice.registerId()!,
      transactionData,
    );

    this.#uuid.set(parkedUuid);

    return {
      uuid: parkedUuid,
      status: TransactionStatus.PARKED,
    } as CreateTransactionResponseDto;
  }

  private async cancelOffline(reason: string) {
    await this.#offlineDb.saveTransaction({
      items: this.itemMapToSerializable(this.items()),
      expectedTotalPayable: this.total(),
      discount: this.promoTransactionDiscount()?.id,
      cashReceived: this.#cashReceived(),
      paymentMethod: this.paymentMethod(),
      customer: this.customer(),
      status: TransactionStatus.CANCELLED,
      synced: 0,
      createdAt: Date.now(),
      createdWhileOffline: true,
      storeId: this.#currentDevice.storeId()!,
      registerId: this.#currentDevice.registerId()!,
      type: 'counter',
      staffId: this.#auth.currentUserId()!,
      salesTaxRate: 12,
      tip: 0,
      note: reason,
    });

    await this.updateOfflineProductStocks();
    await this.updateOfflineRegisterSession();

    this.reset();
  }

  /**
   * Create a live order from a completed transaction
   */
  private async createLiveOrder(
    transaction: CreateTransactionResponseDto,
    transactionData: CreateTransactionRequestDto,
    invoiceData?: PosTransactionInvoice,
  ): Promise<void> {
    try {
      const storeType = this.#currentDevice.store()?.type;
      if (storeType !== StoreType.FNB) return;

      const items = [...this.items().values()];
      const customer = this.customer();
      const customerPhone =
        typeof customer?.mobile === 'string'
          ? customer.mobile
          : customer?.mobile?.number;

      const orderNumber = transaction.id.toString();

      const serviceType =
        transaction.serviceType ?? TransactionServiceType.DINE_IN;

      const fulfillmentType = {
        [TransactionServiceType.DINE_IN]: LiveOrderFulfillmentType.DINE_IN,
        [TransactionServiceType.TAKE_OUT]: LiveOrderFulfillmentType.TAKE_OUT,
      }[serviceType];

      await this.#liveOrdersApi.createLiveOrder({
        storeId: transactionData.storeId,
        transactionUuid: transaction.uuid,
        orderNumber,
        customerName:
          customer?.fullName || transactionData.reference || 'Walk-in Customer',
        customerPhone: customerPhone || undefined,
        fulfillmentType,
        specialInstructions: this.note() ?? undefined,
        items: items.map((item) => ({
          productId: item.product.id,
          productName: item.title,
          quantity: item.quantity,
          contents: item.bundleOptions.map((o) => ({
            quantity: o.qty * item.quantity,
            title: o.title,
          })),
          note: item.note || undefined,
        })),
      });
    } catch (error) {
      console.error('[POS] Live order creation failed:', error);
    }
  }

  /**
   * Update register session with payment amount
   */
  private async updateOfflineRegisterSession(): Promise<void> {
    const registerId = this.#currentDevice.registerId();
    const storeId = this.#currentDevice.storeId();

    if (!registerId || !storeId) return;

    const session = await this.#offlineDb.getRegisterSession(registerId);
    if (!session?.id) return;

    const financials = session.financials;
    const movements = session.movements;
    if (!financials || !movements) return;

    const paymentAmount = this.total();

    if (this.paymentMethod() === PaymentMethod.CASH) {
      financials.cashPaymentsReceived += paymentAmount;
    } else if (this.paymentMethod() === PaymentMethod.GCASH) {
      // TODO: Payment method not yet supported
      // financials.gCashPaymentsReceived += paymentAmount;
    } else if (
      this.paymentMethod() === PaymentMethod.CREDIT_CARD ||
      this.paymentMethod() === PaymentMethod.DEBIT_CARD
    ) {
      // TODO: Payment method not yet supported
      // financials.creditPaymentsReceived += paymentAmount;
    }

    financials.totalPaymentsReceived = financials.cashPaymentsReceived;
    // TODO: Payment method not yet supported
    // financials.gCashPaymentsReceived +
    // financials.creditPaymentsReceived;

    let netCashMovements = 0;
    for (const mov of movements) {
      if (
        mov.type === CashMovementType.CASH_IN ||
        mov.type === CashMovementType.STARTING_CASH
      ) {
        netCashMovements += mov.amount;
      } else if (mov.type === CashMovementType.CASH_OUT) {
        netCashMovements -= mov.amount;
      }
    }
    financials.expectedCash =
      netCashMovements + financials.cashPaymentsReceived;

    financials.grossTotal = netCashMovements + financials.totalPaymentsReceived;

    await this.#offlineDb.saveRegisterSession(registerId, storeId, session);
  }

  /**
   * Update product stocks locally after a transaction
   */
  private async updateOfflineProductStocks(): Promise<void> {
    const storeId = this.#currentDevice.storeId();
    if (!storeId) return;

    const items = this.items();
    const stockUpdates: { productId: number; stock: number | null }[] = [];

    for (const [_, item] of items) {
      const productId = item.product.id;

      const currentStockSignal = this.#stockGateway.getProductStock(productId);
      const currentStock = currentStockSignal();

      if (currentStock !== null && currentStock !== undefined) {
        const newStock = Math.max(0, currentStock - item.quantity);
        stockUpdates.push({ productId, stock: newStock });

        this.#stockGateway.updateProductStock(productId, newStock);
      }
    }

    if (stockUpdates.length > 0) {
      await this.#offlineDb.saveProductStocks(storeId, stockUpdates);
    }
  }

  private getItemStatutoryDiscount(
    item: CurrentTransactionItem,
    canApplyBnpcDiscount: boolean,
  ) {
    const productDiscountIds = item.product.statutoryDiscountIds;

    // Handle case where product doesn't have statutoryDiscountIds
    if (!productDiscountIds || !Array.isArray(productDiscountIds)) {
      return null;
    }

    for (const productDiscountId of productDiscountIds) {
      if (
        productDiscountId === this.BNPC_DISCOUNT_ID &&
        !canApplyBnpcDiscount
      ) {
        continue;
      }

      const discount = this.#customerStatutoryDiscounts().find(
        (d) => d.id === productDiscountId,
      );

      if (!discount) continue;

      return { ...discount };
    }
    return null;
  }

  private getItemDiscountMode(
    item: CurrentTransactionItem,
  ): CurrentTransactionDiscountMode {
    if (
      (item.promoItemDiscount || item.promoTransactionDiscount) &&
      item.statutoryDiscount
    ) {
      return item.lessPromoDiscount >
        item.lessStatutoryDiscount + item.lessStatutoryTaxExempted
        ? 'promo'
        : 'statutory';
    } else if (item.statutoryDiscount) {
      return 'statutory';
    } else {
      return 'promo';
    }
  }

  private itemMapToSerializable(
    itemMap: Map<string, CurrentTransactionItem>,
  ): CurrentTransactionSerializedItemEntry[] {
    return Array.from(itemMap.entries()).map(([key, value]) => {
      return [key, value.toSerializable() as CurrentTransactionSerializedItem];
    });
  }

  private serializableToItemMap(
    serializableItems: CurrentTransactionSerializedItemEntry[],
  ): Map<string, CurrentTransactionItem> {
    const map = new Map<string, CurrentTransactionItem>();
    serializableItems.forEach(([key, value]) => {
      const item = CurrentTransactionItem.fromSerializable(value);
      map.set(key, item);
    });
    return map;
  }

  private async refreshCustomerStatutoryDiscounts() {
    const customer = this.customer();

    if (
      !customer ||
      !customer.id ||
      !customer.statutoryDiscount ||
      customer.statutoryDiscount.status ===
        CustomerStatutoryDiscountStatus.EXPIRED
    ) {
      this.setCustomerStatutoryDiscounts([]);
      return;
    }

    const discounts = await this.#customerApi.findCustomerStatutoryDiscounts(
      customer.id,
    );
    this.setCustomerStatutoryDiscounts(discounts);
  }

  private toCreateTransactionRequestDto(
    signedChallenge: DeviceAuthenticatedRequestDto,
  ): CreateTransactionRequestDto {
    const serviceType = this.serviceType();
    const note = this.note();
    const reference = this.reference();
    const qrphData = this.#qrphData();

    return {
      storeId: this.#currentDevice.storeId()!,
      registerId: this.#currentDevice.registerId()!,
      staffId: this.#auth.currentUserId()!,

      customerId: this.customer()?.id,
      discountId: this.promoTransactionDiscount()?.id,

      paymentMethod: this.paymentMethod(),
      cashReceived: this.#cashReceived(),
      expectedTotalPayable: this.total(),
      bnpcRemainingBalanceThisWeek: this.bnpcRemainingThisWeek() ?? 0,
      bnpcItemsCountThisWeek: this.bnpcItemsCountThisWeek() ?? 0,
      status: this.status(),
      tip: 0, // TODO

      ...(serviceType && { serviceType }),
      ...(note && { note }),
      ...(reference && { reference }),
      ...(qrphData && { qrphData }),

      items: Array.from(this.items().values(), (item) => ({
        productId: item.product.id,
        modifierOptionIds: (item.product.selectedModifierOptions ?? []).map(
          ({ id }) => id,
        ),
        bundleOptions: (item.product.selectedBundleOptions ?? []).map((i) => ({
          id: i.id,
          quantity: i.quantity,
          modifierOptionIds: (i.selectedModifierOptions ?? []).map(
            ({ id }) => id,
          ),
        })),
        quantity: item.quantity,
        discountId: item.promoItemDiscount?.id,
        ...(item.note && { note: item.note }),
      })),

      ...signedChallenge,
    };
  }

  private toParkTransactionRequestDto(): ParkTransactionRequestDto {
    const serviceType = this.serviceType();
    const note = this.note();
    const reference = this.reference();
    const qrphData = this.#qrphData();

    return {
      storeId: this.#currentDevice.storeId()!,
      registerId: this.#currentDevice.registerId()!,
      staffId: this.#auth.currentUserId()!,

      customerId: this.customer()?.id,
      discountId: this.promoTransactionDiscount()?.id,

      paymentMethod: this.paymentMethod(),
      bnpcRemainingBalanceThisWeek: this.bnpcRemainingThisWeek() ?? 0,
      bnpcItemsCountThisWeek: this.bnpcItemsCountThisWeek() ?? 0,
      tip: 0, // TODO

      ...(serviceType && { serviceType }),
      ...(note && { note }),
      ...(reference && { reference }),
      ...(qrphData && { qrphData }),

      items: Array.from(this.items().values(), (item) => ({
        productId: item.product.id,
        modifierOptionIds: (item.product.selectedModifierOptions ?? []).map(
          ({ id }) => id,
        ),
        bundleOptions: (item.product.selectedBundleOptions ?? []).map((i) => ({
          id: i.id,
          quantity: i.quantity,
          modifierOptionIds: (i.selectedModifierOptions ?? []).map(
            ({ id }) => id,
          ),
        })),
        quantity: item.quantity,
        discountId: item.promoItemDiscount?.id,
        ...(item.note && { note: item.note }),
      })),
    };
  }

  private toPreviewTransactionTotalsRequestDto(): PreviewTransactionTotalsRequestDto {
    // TODO: Reduce data sent to endpoint to include only those that are
    //       are needed to preview txn totals
    const serviceType = this.serviceType();
    const note = this.note();
    const reference = this.reference();
    const qrphData = this.#qrphData();

    return {
      storeId: this.#currentDevice.storeId()!,
      registerId: this.#currentDevice.registerId()!,
      staffId: this.#auth.currentUserId()!,

      customerId: this.customer()?.id,
      discountId: this.promoTransactionDiscount()?.id,

      paymentMethod: this.paymentMethod(),
      cashReceived: this.#cashReceived(),
      expectedTotalPayable: this.total(),
      bnpcRemainingBalanceThisWeek: this.bnpcRemainingThisWeek() ?? 0,
      bnpcItemsCountThisWeek: this.bnpcItemsCountThisWeek() ?? 0,
      status: this.status(),
      tip: 0, // TODO

      ...(serviceType && { serviceType }),
      ...(note && { note }),
      ...(reference && { reference }),
      ...(qrphData && { qrphData }),

      items: Array.from(this.items().values(), (item) => ({
        productId: item.product.id,
        modifierOptionIds: (item.product.selectedModifierOptions ?? []).map(
          ({ id }) => id,
        ),
        bundleOptions: (item.product.selectedBundleOptions ?? []).map((i) => ({
          id: i.id,
          quantity: i.quantity,
          modifierOptionIds: (i.selectedModifierOptions ?? []).map(
            ({ id }) => id,
          ),
        })),
        quantity: item.quantity,
        discountId: item.promoItemDiscount?.id,
        ...(item.note && { note: item.note }),
      })),
    };
  }

  private toUnparkTransactionRequestDto(): UnparkTransactionRequestDto {
    return {
      cashReceived: this.#cashReceived(),
      expectedTotalPayable: this.total(),
    };
  }
}
